import clsx from 'clsx';
import { isNativeAppWebview } from 'core';
import { useTranslate } from 'core/hooks/useTranslate';
import { useUser } from 'core/lite/hooks/useUser';
import TextLink from 'core/modules/atoms/TextLink';
import PropTypes from 'prop-types';
import React from 'react';
import './AnalyticsErrorState.scss';

/**
 * Uses the same visual style as ContentNotFoundView
 * @param {Object} props
 * @param {'no-courses' | 'empty' | 'error'} props.variant - The variant to display:
 *   - 'no-courses': Teacher doesn't have Math/Language courses
 *   - 'empty': Has courses but no analytics data yet
 *   - 'error': Unexpected error or malformed data
 */
const AnalyticsErrorState = ({ variant = 'empty' }) => {
  const { t } = useTranslate();
  const { user } = useUser();

  function closeWebview() {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage('closeWebview');
    }
  }

  // Determine title and subtitle based on variant
  const getContent = () => {
    if (variant === 'no-courses') {
      return {
        title: t('chatbot:analytics_no_eligible_courses'),
        subtitle: t('chatbot:analytics_only_math_language'),
      };
    }
    if (variant === 'error') {
      return {
        title: t('chatbot:analytics_error_title'),
        subtitle: t('chatbot:analytics_error_subtitle'),
      };
    }
    // variant === 'empty' (default)
    return {
      title: t('chatbot:analytics_empty_title'),
      subtitle: t('chatbot:analytics_empty_subtitle'),
    };
  };

  const { title, subtitle } = getContent();

  return (
    <div className="analytics-error-state">
      <div className="analytics-error-state__content">
        <div className="analytics-error-state__image">
          <img src="" className={clsx('analytics-error-state__image-error', 'analytics-error-state__image-error--teacher')} alt="Error" />
        </div>
        <div className="analytics-error-state__texts">
          <span className="analytics-error-state__texts-title">{title}</span>
          <span className="analytics-error-state__texts-subtitle">{subtitle}</span>
        </div>
        {user?.source !== 'lti' && (
          <div className="analytics-error-state__back">
            <TextLink
              onClick={isNativeAppWebview() ? closeWebview : undefined}
              to={isNativeAppWebview() ? undefined : '/home'}
              text={t('common:Go to homepage')}
              icon="arrow-back"
            />
          </div>
        )}
      </div>
    </div>
  );
};

AnalyticsErrorState.propTypes = {
  variant: PropTypes.oneOf(['no-courses', 'empty', 'error']),
};

AnalyticsErrorState.defaultProps = {
  variant: 'empty',
};

export default AnalyticsErrorState;
